// @file Make sections sortable by drag and drop on Map.
import type { HTMLStyleElementWrapper } from '@@/bits/css'
import { appendCssToDocumentHead } from '@@/bits/css'
import type { SectionDragEvent } from '@@/bits/surface_sortable'
import {
  createAndInsertCloneAtOriginalPosition,
  getIndicatorColor,
  setDirectionForDraggingElement,
  toSectionDragEvent,
} from '@@/bits/surface_sortable'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useForceChangeCursor } from '@@/vuecomposables/force_change_cursor'
import { storeToRefs } from 'pinia'
import type { SortableEvent } from 'sortablejs'
import { computed } from 'vue'

const POST_LIST_ID = 'surface-map-post-list'
const INDICATOR_LINE_CLASS = 'sortable-indicator-line'
const SECTION_SELECTOR = '.surface-section'

export function useSurfaceSectionsDragSortableMap({
  onDragStart,
  onDragEnd,
}: {
  onDragStart?: (e: SectionDragEvent) => void
  onDragEnd?: (e: SectionDragEvent) => void
}): {
  onSectionDragClone: typeof onSectionDragClone
  onSectionDragStart: typeof onSectionDragStart
  onSectionDragEnd: typeof onSectionDragEnd
} {
  const { forceChangeCursor, resetCursor } = useForceChangeCursor()
  const { postListPosition } = storeToRefs(useSurfaceMapStore())
  const { backgroundLuminance, dir } = storeToRefs(useSurfaceStore())
  const indicatorColor = computed(() => getIndicatorColor(backgroundLuminance.value === 'dark'))

  let persistPostListHeightStyleTag: HTMLStyleElementWrapper | null = null
  let postListBoxSizingStyleTag: HTMLStyleElementWrapper | null = null
  let transformPostsInGroupStyleTag: HTMLStyleElementWrapper | null = null
  let hidePostsInCurrentGroupStyleTag: HTMLStyleElementWrapper | null = null
  let hideStickySectionHeaderStyleTag: HTMLStyleElementWrapper | null = null
  let hideSectionActionElementsStyleTag: HTMLStyleElementWrapper | null = null
  let postListPaddingStyleTag: HTMLStyleElementWrapper | null = null
  let postListPaddingBottomStyleTag: HTMLStyleElementWrapper | null = null
  let sectionMarginStyleTag: HTMLStyleElementWrapper | null = null
  let indicatorLineTransformationStyleTag: HTMLStyleElementWrapper | null = null
  let postListTopOnStart = 0
  let postListTopOnEnd = 0
  let postListLeftOnStart = 0
  let postListLeftOnEnd = 0
  let persistentCloneWrapperElement: HTMLElement | null = null
  let persistentCloneHeaderElement: HTMLElement | null = null

  /**
   * (onStart) Since we hide posts while dragging sections, the post list height will
   * be the sum of the sections' heights only. We want to persist the original post
   * list height so the UI doesn't jump too much.
   */
  function persistPostListHeight(): void {
    const postListHeight = document.getElementById(POST_LIST_ID)?.clientHeight ?? 0
    if (postListHeight === 0) return
    persistPostListHeightStyleTag = appendCssToDocumentHead(`
    #${POST_LIST_ID} {
      height: ${postListHeight}px !important;
    }
    `)
  }

  /**
   * (onStart) Custom styles for the dragging UI.
   */
  function addVerticalDraggingStyles(): void {
    transformPostsInGroupStyleTag = appendCssToDocumentHead(`
    .group-posts {
      visibility: hidden;
      height: 0;
      margin: 0;
    }
  `)
    hideSectionActionElementsStyleTag = appendCssToDocumentHead(`
    .surface-section-action-buttons, .surface-section-divider {
      visibility: hidden;
      height: 0;
      margin: 0;
    }
    `)
    sectionMarginStyleTag = appendCssToDocumentHead(`
      ${SECTION_SELECTOR} {
        margin-bottom: 8px;
      }
    `)
    indicatorLineTransformationStyleTag = appendCssToDocumentHead(`
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} {
        visibility: hidden;
        height: 0;
        position: relative;
        overflow: visible !important;
        margin: 0 !important;
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} > header {
        visibility: hidden;
        height: 2px;
        position: absolute;
        overflow: visible !important;
        ${dir.value === 'rtl' ? 'right' : 'left'}: 6px;
        margin: 0 !important;
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} > header::before {
        content: '';
        visibility: visible;
        border-bottom: 2px solid ${indicatorColor.value};
        height: 0;
        width: calc(${persistentCloneHeaderElement?.getBoundingClientRect().width ?? 0}px + 12px);
        position: absolute;
        top: 0;
        left: auto;
        right: auto;
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} > header > div {
        display: none;
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} > header > div:first-child {
        position: unset !important;
        display: flex;
        height: 0;
        width: calc(${persistentCloneHeaderElement?.getBoundingClientRect().width ?? 0}px + 12px);
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} > header > div:first-child::before {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        top: -2px;
        ${dir.value === 'rtl' ? 'right' : 'left'}: 0;
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} > header > div:first-child::after {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        top: -2px;
        ${dir.value === 'rtl' ? 'left' : 'right'}: 4px;
      }
    `)
  }

  /**
   * (onStart) Custom styles for the dragging UI.
   */
  function addHorizontalDraggingStyles(): void {
    transformPostsInGroupStyleTag = appendCssToDocumentHead(`
    .group-posts {
      max-width: 328px;
    }
  `)
    hidePostsInCurrentGroupStyleTag = appendCssToDocumentHead(`
    ${SECTION_SELECTOR}.sortable-dragging-clone > .group-posts {
      visibility: hidden;
      height: 0;
      margin: 0;
    }
  `)
    hideStickySectionHeaderStyleTag = appendCssToDocumentHead(`
    .surface-group-sticky-header, .surface-group-right-header {
      visibility: hidden;
      height: 0;
      margin: 0;
    }
    `)
    indicatorLineTransformationStyleTag = appendCssToDocumentHead(`
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} {
        visibility: hidden;
        width: 0;
        position: relative;
        overflow: visible !important;
        margin: 0 !important;
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS}::before {
        content: '';
        visibility: visible;
        border-right: 2px solid ${indicatorColor.value};
        height: 100%;
        width: 2px;
        position: absolute;
        top: 0;
        ${dir.value === 'rtl' ? 'right' : 'left'}: 3px;
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS}::after {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        bottom: 0;
        ${dir.value === 'rtl' ? 'right' : 'left'}: 3px;
      }
      ${SECTION_SELECTOR}.${INDICATOR_LINE_CLASS} > div:first-child::before {
        content: '';
        visibility: visible;
        position: absolute;
        height: 6px;
        width: 6px;
        background: ${indicatorColor.value};
        border-radius: 6px;
        top: 0;
        ${dir.value === 'rtl' ? 'right' : 'left'}: 3px;
      }
    `)
  }

  /**
   * (onStart) While dragging, neighbor sections will gather around the current section's
   * position. To do that, we add temporary paddings.
   */
  function addTemporaryPostListPadding(padding: number, position: 'left' | 'top' | 'right' | 'bottom'): void {
    if (padding <= 0) return
    if (position === 'left') {
      postListPaddingStyleTag = appendCssToDocumentHead(`
        #${POST_LIST_ID} {
          padding-left: ${padding}px;
        }
      `)
    } else if (position === 'top') {
      postListPaddingStyleTag = appendCssToDocumentHead(`
        #${POST_LIST_ID} {
          padding-top: ${padding}px;
        }
      `)
    } else if (position === 'right') {
      postListPaddingBottomStyleTag = appendCssToDocumentHead(`
        #${POST_LIST_ID} {
          padding-right: ${padding}px;
        }
      `)
    } else {
      postListPaddingBottomStyleTag = appendCssToDocumentHead(`
        #${POST_LIST_ID} {
          padding-bottom: ${padding}px;
        }
      `)
    }
  }

  /**
   * (onEnd) Remove every custom styles we added for the dragging UI.
   */
  function removeAllDraggingStyles(): void {
    persistPostListHeightStyleTag?.remove()
    persistPostListHeightStyleTag = null
    postListBoxSizingStyleTag?.remove()
    postListBoxSizingStyleTag = null
    transformPostsInGroupStyleTag?.remove()
    transformPostsInGroupStyleTag = null
    hidePostsInCurrentGroupStyleTag?.remove()
    hidePostsInCurrentGroupStyleTag = null
    hideStickySectionHeaderStyleTag?.remove()
    hideStickySectionHeaderStyleTag = null
    hideSectionActionElementsStyleTag?.remove()
    hideSectionActionElementsStyleTag = null
    sectionMarginStyleTag?.remove()
    sectionMarginStyleTag = null
    indicatorLineTransformationStyleTag?.remove()
    indicatorLineTransformationStyleTag = null
    postListPaddingStyleTag?.remove()
    postListPaddingStyleTag = null
    postListPaddingBottomStyleTag?.remove()
    postListPaddingBottomStyleTag = null
  }

  /**
   * (onStart) We remember the scroll position so after dragging the
   * dragged section is still at the same mouse position.
   */
  function rememberPostListTopPositionOnStart(e: SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const { top } = el.getBoundingClientRect()
    postListTopOnStart = top
  }

  /**
   * (onStart) Restore the scroll position after applying styles.
   */
  function recoverPostListTopPositionOnStart(e: SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const scrollingElement = document.getElementById(POST_LIST_ID)
    if (scrollingElement == null) return
    const shouldAddBottomPostListPadding = scrollingElement.clientHeight !== scrollingElement.scrollHeight
    if (!shouldAddBottomPostListPadding) {
      postListBoxSizingStyleTag = appendCssToDocumentHead(`
        #${POST_LIST_ID} {
          box-sizing: border-box;
        }
      `)
    }
    const { top } = el.getBoundingClientRect()
    const offset = postListTopOnStart - top
    const newScrollTop = scrollingElement.scrollTop - offset
    if (newScrollTop < 0) {
      addTemporaryPostListPadding(-newScrollTop, 'top')
    }
    if (shouldAddBottomPostListPadding && newScrollTop + window.innerHeight > scrollingElement.scrollHeight) {
      addTemporaryPostListPadding(scrollingElement.scrollHeight - newScrollTop - window.innerHeight, 'bottom')
    }
    scrollingElement.scrollTop = newScrollTop
  }

  /**
   * @see `rememberPostListTopPositionOnStart`
   */
  function rememberPostListTopPositionOnEnd(e: SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    postListTopOnEnd = el.getBoundingClientRect().top
  }

  /**
   * @see `recoverPostListTopPositionOnStart`
   */
  function recoverPostListTopPositionOnEnd(e: SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const scrollingElement = document.getElementById(POST_LIST_ID)
    if (scrollingElement == null) return
    const { top } = el.getBoundingClientRect()
    const offset = postListTopOnEnd - top
    const newScrollTop = scrollingElement.scrollTop - offset
    scrollingElement.scrollTop = newScrollTop
  }

  /**
   * (onStart) We remember the scroll position so after dragging the
   * dragged section is still at the same mouse position.
   */
  function rememberPostListLeftPositionOnStart(e: SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const { left } = el.getBoundingClientRect()
    postListLeftOnStart = left
  }

  /**
   * (onStart) Restore the scroll position after applying styles.
   */
  function recoverPostListLeftPositionOnStart(e: SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const scrollingElement = document.getElementById(POST_LIST_ID)
    if (scrollingElement == null) return
    const { left } = el.getBoundingClientRect()
    const offset = postListLeftOnStart - left
    const newScrollLeft = scrollingElement.scrollLeft - offset
    if (newScrollLeft < 0) {
      addTemporaryPostListPadding(-newScrollLeft, 'left')
    }
    if (newScrollLeft + window.innerWidth > scrollingElement.scrollWidth) {
      addTemporaryPostListPadding(scrollingElement.scrollWidth - newScrollLeft - window.innerWidth, 'right')
    }
    scrollingElement.scrollLeft = newScrollLeft
  }

  /**
   * @see `rememberPostListLeftPositionOnStart`
   */
  function rememberPostListLeftPositionOnEnd(e: SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    postListLeftOnEnd = el.getBoundingClientRect().left
  }

  /**
   * @see `recoverPostListTopPositionOnStart`
   */
  function recoverPostListLeftPositionOnEnd(e: SortableEvent): void {
    const el = persistentCloneWrapperElement ?? e.item
    const scrollingElement = document.getElementById(POST_LIST_ID)
    if (scrollingElement == null) return
    const { left } = el.getBoundingClientRect()
    const offset = postListLeftOnEnd - left
    const newScrollLeft = scrollingElement.scrollLeft - offset
    scrollingElement.scrollLeft = newScrollLeft
  }

  /**
   * (onStart) Create a clone of the dragging section
   * (showing the original position of the dragging section).
   */
  function createPersistentClone(e: SortableEvent): void {
    const { cloneWrapperElement, cloneTargetElement } = createAndInsertCloneAtOriginalPosition(
      e,
      '.surface-group-title',
    )
    persistentCloneWrapperElement = cloneWrapperElement
    persistentCloneHeaderElement = cloneTargetElement
  }

  /**
   * (onEnd) Remove the clone of the dragging section.
   */
  function removePersistentCloneWrapperElement(): void {
    persistentCloneWrapperElement?.remove()
    persistentCloneWrapperElement = null
  }

  function onSectionDragClone(): void {
    setDirectionForDraggingElement(dir.value)
  }

  function onSectionDragStart(e: SortableEvent): void {
    onDragStart?.(toSectionDragEvent(e))
    forceChangeCursor('grabbing')
    if (postListPosition.value === 'side') {
      persistPostListHeight()
      rememberPostListTopPositionOnStart(e)
      createPersistentClone(e)
      addVerticalDraggingStyles()
      recoverPostListTopPositionOnStart(e)
    } else {
      rememberPostListLeftPositionOnStart(e)
      createPersistentClone(e)
      addHorizontalDraggingStyles()
      recoverPostListLeftPositionOnStart(e)
    }
  }

  function onSectionDragEnd(e: SortableEvent): void {
    if (postListPosition.value === 'side') {
      removePersistentCloneWrapperElement()
      rememberPostListTopPositionOnEnd(e)
      removeAllDraggingStyles()
      recoverPostListTopPositionOnEnd(e)
    } else {
      removePersistentCloneWrapperElement()
      rememberPostListLeftPositionOnEnd(e)
      removeAllDraggingStyles()
      recoverPostListLeftPositionOnEnd(e)
    }
    resetCursor()
    onDragEnd?.(toSectionDragEvent(e))
  }

  return { onSectionDragClone, onSectionDragStart, onSectionDragEnd }
}
